import React, { useEffect, useRef, useCallback } from "react";
import { publicMeetingAPI } from "../services/api_v1";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "./ui/select";

import { useToast } from "./ui/toast";
import { useMeetingStore, MediaStates, Meeting, MediaDeviceInfo } from "../stores/meetingStore";
import {
	Video,
	VideoOff,
	Mic,
	MicOff,
	Users,
	Loader2,
	AlertCircle,
	Volume2,
	Smartphone,

	Sparkles,
	Monitor,
	Signal,
	Wifi,
} from "lucide-react";
import { cn } from "../lib/utils";
import { createLocalVideoTrack, createLocalAudioTrack, VideoPresets } from "livekit-client";
import MicIndicator from "./MicIndicator";

interface PublicMeetingOnboardingProps {
	meetingId: string;
	token: string;
	onJoinMeeting: (mediaStates: MediaStates) => void;
}

const PublicMeetingOnboarding: React.FC<PublicMeetingOnboardingProps> = ({
	meetingId,
	token,
	onJoinMeeting,
}) => {
	// Zustand store selectors
	const participantData = useMeetingStore((state) => state.participantData);
	const meeting = useMeetingStore((state) => state.meeting);
	const currentMediaStates = useMeetingStore((state) => state.currentMediaStates);
	const alreadyJoined = useMeetingStore((state) => state.alreadyJoined);
	const cameraPermissionGranted = useMeetingStore((state) => state.cameraPermissionGranted);
	const micPermissionGranted = useMeetingStore((state) => state.micPermissionGranted);
	const loading = useMeetingStore((state) => state.loading);
	const error = useMeetingStore((state) => state.error);
	const isJoining = useMeetingStore((state) => state.isJoining);
	const audioInputDevices = useMeetingStore((state) => state.audioInputDevices);
	const audioOutputDevices = useMeetingStore((state) => state.audioOutputDevices);
	const selectedAudioInput = useMeetingStore((state) => state.selectedAudioInput);
	const selectedAudioOutput = useMeetingStore((state) => state.selectedAudioOutput);
	const isTestingSound = useMeetingStore((state) => state.isTestingSound);
	const videoQuality = useMeetingStore((state) => state.videoQuality);
	const facingMode = useMeetingStore((state) => state.facingMode);

	const videoInputDevices = useMeetingStore((state) => state.videoInputDevices);
	const activeTab = useMeetingStore((state) => state.activeTab);

	// Zustand store actions
	const setParticipantData = useMeetingStore((state) => state.setParticipantData);
	const setMeeting = useMeetingStore((state) => state.setMeeting);
	const setCurrentMediaStates = useMeetingStore((state) => state.setCurrentMediaStates);
	const setAlreadyJoined = useMeetingStore((state) => state.setAlreadyJoined);
	const setCameraPermissionGranted = useMeetingStore((state) => state.setCameraPermissionGranted);
	const setMicPermissionGranted = useMeetingStore((state) => state.setMicPermissionGranted);
	const setLoading = useMeetingStore((state) => state.setLoading);
	const setError = useMeetingStore((state) => state.setError);
	const setIsJoining = useMeetingStore((state) => state.setIsJoining);
	const setAudioInputDevices = useMeetingStore((state) => state.setAudioInputDevices);
	const setAudioOutputDevices = useMeetingStore((state) => state.setAudioOutputDevices);
	const setSelectedAudioInput = useMeetingStore((state) => state.setSelectedAudioInput);
	const setSelectedAudioOutput = useMeetingStore((state) => state.setSelectedAudioOutput);
	const setIsTestingSound = useMeetingStore((state) => state.setIsTestingSound);
	const setVideoQuality = useMeetingStore((state) => state.setVideoQuality);
	const setFacingMode = useMeetingStore((state) => state.setFacingMode);

	const setVideoInputDevices = useMeetingStore((state) => state.setVideoInputDevices);
	const setActiveTab = useMeetingStore((state) => state.setActiveTab);

	// Refs for media streams
	const micStreamRef = useRef<MediaStream | null>(null);
	const previewStreamRef = useRef<MediaStream | null>(null);
	const videoRef = useRef<HTMLVideoElement>(null);

	const { addToast } = useToast();

	// Detect if device is mobile (has multiple cameras)
	const isMobile = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(
		(navigator.userAgent || navigator.vendor || (window as any).opera).toLowerCase()
	);

	const initializeOnboarding = useCallback(async () => {
		try {
			setLoading(true);
			setError("");
			setAlreadyJoined(false);

			// Get participant data by token
			const participantResponse = await publicMeetingAPI.getParticipantByToken(meetingId, token);

			if (!participantResponse.data.success) {
				throw new Error("Invalid token or meeting not found");
			}

			const data = participantResponse.data.data;
			setParticipantData(data);
			setMeeting(data.meeting);

		} catch (error: any) {
			console.error("Failed to initialize onboarding:", error);
			setError(error.response?.data?.message || "Failed to load meeting");
		} finally {
			setLoading(false);
		}
	}, [meetingId, token]);



	useEffect(() => {
		initializeOnboarding();
		// Check initial permissions
		checkCameraPermission();
		checkMicrophonePermission();
	}, [initializeOnboarding]); // Added initializeOnboarding to dependencies

	const checkCameraPermission = async () => {
		try {
			if (navigator.permissions) {
				const permission = await navigator.permissions.query({
					name: "camera" as PermissionName,
				});

				const updatePermissionState = () => {
					if (permission.state === "denied") {
						setCameraPermissionGranted(false);
					} else if (permission.state === "granted") {
						setCameraPermissionGranted(true);
					} else {
						setCameraPermissionGranted(null);
					}
				};

				updatePermissionState();

				// Listen for permission changes
				permission.onchange = updatePermissionState;
			}
		} catch (error) {
			console.error("Error checking camera permission:", error);
		}
	};

	const checkMicrophonePermission = async () => {
		try {
			if (navigator.permissions) {
				const permission = await navigator.permissions.query({
					name: "microphone" as PermissionName,
				});

				const updatePermissionState = () => {
					if (permission.state === "denied") {
						setMicPermissionGranted(false);
					} else if (permission.state === "granted") {
						setMicPermissionGranted(true);
					} else {
						setMicPermissionGranted(null);
					}
				};

				updatePermissionState();

				// Listen for permission changes
				permission.onchange = updatePermissionState;
			}
		} catch (error) {
			console.error("Error checking microphone permission:", error);
		}
	};

	// Enumerate available audio devices
	const enumerateDevices = useCallback(async () => {
		try {
			const devices = await navigator.mediaDevices.enumerateDevices();

			const audioInputs: MediaDeviceInfo[] = devices
				.filter(device => device.kind === "audioinput" && device.deviceId)
				.map(device => ({
					deviceId: device.deviceId,
					label: device.label || `Microphone ${device.deviceId.substring(0, 5)}`,
					kind: device.kind,
				}));

			const audioOutputs: MediaDeviceInfo[] = devices
				.filter(device => device.kind === "audiooutput" && device.deviceId)
				.map(device => ({
					deviceId: device.deviceId,
					label: device.label || `Speaker ${device.deviceId.substring(0, 5)}`,
					kind: device.kind,
				}));

			const videoInputs: MediaDeviceInfo[] = devices
				.filter(device => device.kind === "videoinput" && device.deviceId)
				.map(device => ({
					deviceId: device.deviceId,
					label: device.label || `Camera ${device.deviceId.substring(0, 5)}`,
					kind: device.kind,
				}));

			setAudioInputDevices(audioInputs);
			setAudioOutputDevices(audioOutputs);
			setVideoInputDevices(videoInputs);

			// Set default devices if not already selected
			if (!selectedAudioInput && audioInputs.length > 0) {
				setSelectedAudioInput(audioInputs[0].deviceId);
			}
			if (!selectedAudioOutput && audioOutputs.length > 0) {
				setSelectedAudioOutput(audioOutputs[0].deviceId);
			}
		} catch (error) {
			console.error("Error enumerating devices:", error);
		}
	}, [selectedAudioInput, selectedAudioOutput]);

	// Start/update microphone stream for visualization
	const updateMicStream = useCallback(async () => {
		try {
			// Stop any existing mic stream
			if (micStreamRef.current) {
				micStreamRef.current.getTracks().forEach(track => track.stop());
				micStreamRef.current = null;
			}

			const audioTrack = await createLocalAudioTrack({
				deviceId: selectedAudioInput ? { exact: selectedAudioInput } : undefined
			});

			// Convert track to MediaStream for MicIndicator
			const stream = new MediaStream([audioTrack.mediaStreamTrack]);
			micStreamRef.current = stream;
		} catch (error) {
			console.error("Error updating mic stream:", error);
		}
	}, [selectedAudioInput]);

	// Stop microphone stream
	const stopMicStream = useCallback(() => {
		if (micStreamRef.current) {
			micStreamRef.current.getTracks().forEach(track => track.stop());
			micStreamRef.current = null;
		}
	}, []);



	// Test speaker functionality
	const testSpeaker = useCallback(async () => {
		try {
			setIsTestingSound(true);

			// Create a short test tone using AudioContext
			const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
			const oscillator = audioContext.createOscillator();
			const gainNode = audioContext.createGain();

			// Create a MediaStreamDestination to capture the audio
			const destination = audioContext.createMediaStreamDestination();

			oscillator.connect(gainNode);
			gainNode.connect(destination);

			oscillator.frequency.value = 440; // A4 note
			oscillator.type = "sine";

			// Fade in
			gainNode.gain.setValueAtTime(0, audioContext.currentTime);
			gainNode.gain.linearRampToValueAtTime(0.3, audioContext.currentTime + 0.1);

			// Fade out
			gainNode.gain.linearRampToValueAtTime(0, audioContext.currentTime + 0.5);

			// Create an audio element to play through the selected output device
			const audio = new Audio();
			audio.srcObject = destination.stream;

			// Set the output device using setSinkId
			if (selectedAudioOutput && "setSinkId" in audio) {
				try {
					await (audio as any).setSinkId(selectedAudioOutput);
				} catch (e) {
					console.warn("setSinkId not supported or failed:", e);
				}
			}

			// Play the audio
			await audio.play();

			oscillator.start(audioContext.currentTime);
			oscillator.stop(audioContext.currentTime + 0.5);

			setTimeout(() => {
				setIsTestingSound(false);
				audio.pause();
				audio.srcObject = null;
				audioContext.close();
			}, 600);

			// addToast({
			// 	title: "Speaker Test",
			// 	description: "Did you hear the test tone?",
			// 	variant: "default",
			// });
		} catch (error) {
			console.error("Error testing speaker:", error);
			setIsTestingSound(false);
			addToast({
				title: "Speaker test failed",
				description: "Could not play test sound",
				variant: "destructive",
			});
		}
	}, [selectedAudioOutput, addToast]);

	// Update video constraints based on settings
	const updateVideoConstraints = useCallback(async () => {
		if (!currentMediaStates.camera_enabled) return;

		try {
			// Stop existing video tracks
			if (previewStreamRef.current) {
				previewStreamRef.current.getTracks().forEach(track => track.stop());
				previewStreamRef.current = null;
			}


			// Determine resolution based on quality setting
			let resolution = VideoPresets.h720.resolution;
			if (videoQuality === "1080p") {
				resolution = VideoPresets.h1080.resolution;
			} else if (videoQuality === "720p") {
				resolution = VideoPresets.h720.resolution;
			} else if (videoQuality === "360p") {
				resolution = VideoPresets.h360.resolution;
			} else if (videoQuality === "240p") {
				resolution = VideoPresets.h180.resolution;
			}

			const videoTrack = await createLocalVideoTrack({
				facingMode: facingMode as any,
				resolution: resolution
			});

			// Convert track to MediaStream for video element
			const newStream = new MediaStream([videoTrack.mediaStreamTrack]);
			previewStreamRef.current = newStream;
			if (videoRef.current) {
				videoRef.current.srcObject = newStream;
				// Ensure video plays
				videoRef.current.play().catch(err => console.log('Video autoplay prevented:', err));
			}

		} catch (error) {
			console.error("Error updating video constraints:", error);
			addToast({
				title: "Video settings error",
				description: "Could not apply video settings",
				variant: "destructive",
			});
		}
	}, [currentMediaStates.camera_enabled, videoQuality, facingMode, addToast]);

	// Effect to update video when settings change
	useEffect(() => {
		if (currentMediaStates.camera_enabled) {
			updateVideoConstraints();
		}
	}, [videoQuality, facingMode, updateVideoConstraints]);

	// Cleanup preview stream on unmount
	useEffect(() => {
		return () => {
			if (previewStreamRef.current) {
				previewStreamRef.current.getTracks().forEach(track => track.stop());
			}
		};
	}, []);

	// Effect to enumerate devices on mount and when permissions change
	useEffect(() => {
		enumerateDevices();

		// Listen for device changes
		navigator.mediaDevices.addEventListener("devicechange", enumerateDevices);

		return () => {
			navigator.mediaDevices.removeEventListener("devicechange", enumerateDevices);
		};
	}, [enumerateDevices]);

	// Effect to update mic stream when microphone is enabled or device changes
	useEffect(() => {
		if (currentMediaStates.microphone_enabled && micPermissionGranted) {
			updateMicStream();
		} else {
			stopMicStream();
		}

		return () => {
			stopMicStream();
		};
	}, [currentMediaStates.microphone_enabled, micPermissionGranted, selectedAudioInput, updateMicStream, stopMicStream]);




	const toggleCamera = async () => {
		// If permission not granted, request it only if we are turning it on
		if (!currentMediaStates.camera_enabled && cameraPermissionGranted !== true) {
			try {
				// Determine resolution based on quality setting
				let resolution = VideoPresets.h720.resolution;
				if (videoQuality === "1080p") {
					resolution = VideoPresets.h1080.resolution;
				} else if (videoQuality === "720p") {
					resolution = VideoPresets.h720.resolution;
				} else if (videoQuality === "360p") {
					resolution = VideoPresets.h360.resolution;
				} else if (videoQuality === "240p") {
					resolution = VideoPresets.h180.resolution;
				}

				const videoTrack = await createLocalVideoTrack({
					facingMode: facingMode as any,
					resolution: resolution
				});

				setCameraPermissionGranted(true);
				const stream = new MediaStream([videoTrack.mediaStreamTrack]);
				previewStreamRef.current = stream;
				if (videoRef.current) {
					videoRef.current.srcObject = stream;
					// Ensure video plays
					videoRef.current.play().catch(err => console.log('Video autoplay prevented:', err));
				}
				// Refresh device list after permission granted
				await enumerateDevices();
			} catch (error) {
				console.error("Error requesting camera permission:", error);
				setCameraPermissionGranted(false);
				addToast({
					title: "Camera access denied",
					description: "Please allow camera access to join the meeting.",
					variant: "destructive",
				});
				return;
			}
		} else if (currentMediaStates.camera_enabled) {
			// Turning off camera
			if (previewStreamRef.current) {
				previewStreamRef.current.getTracks().forEach(track => track.stop());
				previewStreamRef.current = null;
				if (videoRef.current) {
					videoRef.current.srcObject = null;
				}
			}
		} else {
			// Turning on camera (permission already granted)
			try {
				// Determine resolution based on quality setting
				let resolution = VideoPresets.h720.resolution;
				if (videoQuality === "1080p") {
					resolution = VideoPresets.h1080.resolution;
				} else if (videoQuality === "720p") {
					resolution = VideoPresets.h720.resolution;
				} else if (videoQuality === "360p") {
					resolution = VideoPresets.h360.resolution;
				} else if (videoQuality === "240p") {
					resolution = VideoPresets.h180.resolution;
				}

				const videoTrack = await createLocalVideoTrack({
					facingMode: facingMode as any,
					resolution: resolution
				});

				const stream = new MediaStream([videoTrack.mediaStreamTrack]);
				previewStreamRef.current = stream;
				if (videoRef.current) {
					videoRef.current.srcObject = stream;
					// Ensure video plays
					videoRef.current.play().catch(err => console.log('Video autoplay prevented:', err));
				}
			} catch (error) {
				console.error("Error starting camera:", error);
				addToast({
					title: "Camera error",
					description: "Could not start camera.",
					variant: "destructive",
				});
				return;
			}
		}

		const newCameraState = !currentMediaStates.camera_enabled;
		setCurrentMediaStates((prev) => ({
			...prev,
			camera_enabled: newCameraState,
		}));
	};
	// Note: Media state will be saved when joining the meeting
	// No need to update database here since there's no active session yet

	const toggleMicrophone = async () => {
		// If permission not granted, request it only if we are turning it on
		if (!currentMediaStates.microphone_enabled && micPermissionGranted !== true) {
			try {
				const audioTrack = await createLocalAudioTrack({ deviceId: undefined });
				// Stop the track immediately after getting permission
				audioTrack.stop();
				setMicPermissionGranted(true);
				// Refresh device list after permission granted
				await enumerateDevices();
				// Proceed to toggle
			} catch (permError) {
				console.error("Microphone permission denied:", permError);
				addToast({
					title: "Microphone permission denied",
					description: "Please allow microphone access in your browser settings",
					variant: "destructive",
				});
				return;
			}
		}

		const newMicState = !currentMediaStates.microphone_enabled;
		setCurrentMediaStates((prev) => ({
			...prev,
			microphone_enabled: newMicState,
		}));

		// Note: Media state will be saved when joining the meeting
		// No need to update database here since there's no active session yet
	};

	const handleJoinMeeting = async () => {
		try {
			setIsJoining(true);

			// Attempt to join the meeting
			const joinResponse = await publicMeetingAPI.joinMeeting(meetingId, token, currentMediaStates);

			// Check if user is already in the meeting
			if (joinResponse.data.message === "Already in meeting") {
				setAlreadyJoined(true);
				setIsJoining(false);
				addToast({
					title: "Already Joined",
					description: "You are already participating in this meeting",
					variant: "destructive",
				});
				return;
			}

			// If successful join, trigger the parent join flow
			if (joinResponse.data.success) {
				onJoinMeeting(currentMediaStates);
			} else {
				throw new Error(joinResponse.data.message || "Failed to join meeting");
			}
		} catch (error: any) {
			console.error("Failed to join meeting:", error);
			setError(error.response?.data?.message || "Failed to join meeting");
			setIsJoining(false);
		}
	};

	// Helper functions for button states
	const getCameraButtonState = () => {
		if (cameraPermissionGranted === false) {
			return {
				variant: "destructive" as const,
				icon: VideoOff,
				label: "Grant camera permission",
				showBadge: true,
				badgeContent: "!",
			};
		}

		if (currentMediaStates.camera_enabled) {
			return {
				variant: "ghost" as const,
				icon: Video,
				label: "Turn off camera",
				showBadge: false,
				badgeContent: "",
			};
		}

		return {
			variant: "destructive" as const,
			icon: VideoOff,
			label: "Turn on camera",
			showBadge: false,
			badgeContent: "",
		};
	};

	const getMicrophoneButtonState = () => {
		if (micPermissionGranted === false) {
			return {
				variant: "destructive" as const,
				icon: MicOff,
				label: "Grant microphone permission",
				showBadge: true,
				badgeContent: "!",
			};
		}

		if (currentMediaStates.microphone_enabled) {
			return {
				variant: "ghost" as const,
				icon: Mic,
				label: "Mute microphone",
				showBadge: false,
				badgeContent: "",
			};
		}

		return {
			variant: "destructive" as const,
			icon: MicOff,
			label: "Unmute microphone",
			showBadge: false,
			badgeContent: "",
		};
	};

	if (loading) {
		return (
			<div className="min-h-screen bg-background flex items-center justify-center theme-transition">
				<Card className="bg-card border border-border shadow-2xl">
					<CardContent className="p-8">
						<div className="flex flex-col items-center space-y-6">
							<div className="relative">
								<div className="w-16 h-16 border-4 border-primary/30 border-t-primary rounded-full animate-spin"></div>
								<img
									src="/logo.svg"
									alt="Logo"
									className="absolute inset-0 m-auto h-8 w-8"
								/>
							</div>
							<div className="text-center space-y-2">
								<h3 className="text-lg font-semibold text-foreground">
									Loading Meeting
								</h3>
								<p className="text-muted-foreground text-sm">
									Preparing your meeting experience...
								</p>
							</div>
						</div>
					</CardContent>
				</Card>
			</div>
		);
	}

	if (error) {
		return (
			<div className="min-h-screen bg-background flex items-center justify-center theme-transition">
				<Card className="bg-card border border-border shadow-2xl max-w-md mx-4">
					<CardHeader>
						<div className="flex items-center space-x-3">
							<div className="p-2 bg-destructive/20 rounded-full">
								<AlertCircle className="h-6 w-6 text-destructive" />
							</div>
							<CardTitle className="text-foreground">Unable to Load Meeting</CardTitle>
						</div>
					</CardHeader>
					<CardContent className="space-y-4">
						<p className="text-foreground">{error}</p>
						<div className="flex flex-col sm:flex-row gap-3">
							<Button
								onClick={() => initializeOnboarding()}
								className="flex-1 bg-primary text-primary-foreground hover:bg-primary/90"
							>
								Try Again
							</Button>
						</div>
					</CardContent>
				</Card>
			</div>
		);
	}

	// Add already joined UI
	if (alreadyJoined) {
		return (
			<div className="min-h-screen bg-background flex items-center justify-center theme-transition">
				<Card className="bg-card border border-border shadow-2xl max-w-md mx-4">
					<CardHeader>
						<div className="flex items-center space-x-3">
							<div className="p-2 bg-destructive/20 rounded-full">
								<AlertCircle className="h-6 w-6 text-destructive" />
							</div>
							<CardTitle className="text-foreground">Already Joined</CardTitle>
						</div>
					</CardHeader>
					<CardContent className="space-y-4">
						<p className="text-foreground">You are already participating in this meeting.</p>
						<p className="text-muted-foreground text-sm">
							If you're experiencing issues, please leave the meeting and rejoin.
						</p>
						<div className="flex flex-col sm:flex-row gap-3">
							<Button
								onClick={() => window.location.reload()}
								className="flex-1 bg-primary text-primary-foreground hover:bg-primary/90"
							>
								Refresh Page
							</Button>
						</div>
					</CardContent>
				</Card>
			</div>
		);
	}

	return (
		<div className="fixed inset-0 bg-black/80 flex items-center justify-center p-0 md:p-6">
			{/* Main Container - Split Screen Layout */}
			<div className="w-full h-full md:h-[80vh] md:max-w-7xl md:rounded-2xl overflow-hidden bg-[#16171a] shadow-[0_0_100px_rgba(0,0,0,1.0),0_0_50px_rgba(59,130,246,0.4)] flex flex-col md:flex-row landscape:flex-row">
				{/* Left Panel - Video Preview (65%) */}
				<div className="relative w-full md:w-[65%] landscape:w-[60%] h-[40vh] md:h-full landscape:h-full bg-black flex items-center justify-center">
					{currentMediaStates.camera_enabled ? (
						<>
							{/* Video Feed */}
							<video
								ref={videoRef}
								autoPlay
								playsInline
								muted
								className={cn(
									"w-full h-full object-cover",
									// Only mirror when using front-facing camera
									facingMode === 'user' && "scale-x-[-1]"
								)}
							/>

							{/* Audio Visualizer (top-right) */}
							{currentMediaStates.microphone_enabled && (
								<MicIndicator audioTrack={micStreamRef.current} className="absolute top-4 left-1/2 transform -translate-x-1/2" />
							)}
						</>
					) : (
						<>
							{/* Camera Off State - Show Avatar */}
							<div className="flex flex-col items-center justify-center gap-4">
								<div className="w-16 h-16 bg-gradient-to-br from-blue-500/20 to-purple-500/20 rounded-full flex items-center justify-center border-4 border-gray-700">
									<span className="text-3xl font-bold text-white">
										{participantData?.participant?.user_name?.charAt(0).toUpperCase() || "H"}
									</span>
								</div>
								<p className="text-gray-400 text-sm">Camera is OFF</p>
							</div>

							{/* Audio Visualizer (top-right) */}
							{currentMediaStates.microphone_enabled && (
								<MicIndicator audioTrack={micStreamRef.current} className="absolute top-4 left-1/2 transform -translate-x-1/2" />
							)}
						</>
					)}

					{/* Overlaid Controls (bottom-center) */}
					<div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 flex items-center gap-4 z-10">
						{/* Microphone Button */}
						{(() => {
							const micState = getMicrophoneButtonState();
							return (
								<button
									onClick={toggleMicrophone}
									className={cn(
										"w-12 h-12 rounded-full flex items-center justify-center transition-all duration-200 shadow-lg",
										micState.variant === "ghost"
											? "bg-gray-700 hover:bg-gray-600 text-white"
											: "bg-red-600 hover:bg-red-700 text-white"
									)}
									aria-label={micState.label}
								>
									<micState.icon className="w-5 h-5" />
									{micState.showBadge && (
										<span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center text-xs font-bold border-2 border-black">
											!
										</span>
									)}
								</button>
							);
						})()}

						{/* Camera Button */}
						{(() => {
							const cameraState = getCameraButtonState();
							return (
								<button
									onClick={toggleCamera}
									className={cn(
										"w-12 h-12 rounded-full flex items-center justify-center transition-all duration-200 shadow-lg relative",
										cameraState.variant === "ghost"
											? "bg-gray-700 hover:bg-gray-600 text-white"
											: "bg-red-600 hover:bg-red-700 text-white"
									)}
									aria-label={cameraState.label}
								>
									<cameraState.icon className="w-5 h-5" />
									{cameraState.showBadge && (
										<span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center text-xs font-bold border-2 border-black">
											!
										</span>
									)}
								</button>
							);
						})()}
					</div>
				</div>

				{/* Right Panel - Settings Sidebar (35%) */}
				<div className="w-full md:w-[35%] landscape:w-[40%] flex-1 min-h-0 bg-[#16171a] flex flex-col">
					{/* Header Section - Compact */}
					<div className="p-6 border-b border-gray-800">
						<div className="flex items-center gap-3">
							{/* Logo */}
							<div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center flex-shrink-0">
								<img
									src="/logo.svg"
									alt="Logo"
									className="w-6 h-6"
								/>
							</div>

							{/* Meeting Info and Status */}
							<div className="flex-1 min-w-0">
								<h2 className="text-white font-semibold text-lg truncate">
									{meeting?.title || "testing"}
								</h2>
								<div className="flex items-center gap-1.5">
									<div className="w-2 h-2 bg-green-500 rounded-full"></div>
									<span className="text-green-400 text-sm">
										Ready to join {participantData?.participant?.user_name || "host"}
									</span>
								</div>
							</div>
						</div>
					</div>

					{/* Tabs Navigation */}
					<div className="flex border-b border-gray-800">
						<button
							onClick={() => setActiveTab("audio")}
							className={cn(
								"flex-1 px-6 py-3 text-sm font-medium transition-colors relative",
								activeTab === "audio"
									? "text-blue-500 bg-gray-800/50"
									: "text-gray-400 hover:text-gray-300"
							)}
						>
							Audio
							{activeTab === "audio" && (
								<div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-500"></div>
							)}
						</button>
						<button
							onClick={() => setActiveTab("video")}
							className={cn(
								"flex-1 px-6 py-3 text-sm font-medium transition-colors relative",
								activeTab === "video"
									? "text-blue-500 bg-gray-800/50"
									: "text-gray-400 hover:text-gray-300"
							)}
						>
							Video
							{activeTab === "video" && (
								<div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-500"></div>
							)}
						</button>
					</div>

					{/* Scrollable Settings Content */}
					<div className="flex-1 overflow-y-auto p-6 space-y-6">
						{/* Audio Tab Content */}
						{activeTab === "audio" && (
							<div className="space-y-6">
								{/* Microphone Dropdown */}
								<div className="space-y-2">
									<label className="text-xs text-gray-400 font-medium uppercase tracking-wider">
										Microphone
									</label>
									<Select
										value={selectedAudioInput}
										onValueChange={(value) => setSelectedAudioInput(value)}
										disabled={!micPermissionGranted}
									>
										<SelectTrigger className="w-full bg-gray-800 border-gray-700 text-white">
											<SelectValue placeholder={micPermissionGranted === false ? "Permission denied" : "Select microphone"} />
										</SelectTrigger>
										<SelectContent className="bg-gray-800 border-gray-700">
											{audioInputDevices.map((device) => (
												<SelectItem key={device.deviceId} value={device.deviceId} className="text-white">
													<span className="flex items-center gap-2 w-full">
														<Mic className="w-4 h-4 shrink-0" />
														<span className="truncate">{device.label}</span>
													</span>
												</SelectItem>
											))}
											{audioInputDevices.length === 0 && (
												<div className="p-2 text-sm text-gray-400 text-center">
													No microphones found
												</div>
											)}
										</SelectContent>
									</Select>
								</div>

								{/* Speaker Dropdown with Test Button */}
								<div className="space-y-2">
									<div className="flex items-center justify-between">
										<label className="text-xs text-gray-400 font-medium uppercase tracking-wider">
											Speaker
										</label>
										<div className="flex items-center gap-2">
											{isTestingSound && (
												<span className="text-xs text-blue-400 animate-pulse">
													Playing test sound...
												</span>
											)}
											<Button
												variant="ghost"
												size="sm"
												onClick={testSpeaker}
												disabled={isTestingSound || !micPermissionGranted}
												title="Test Speaker"
												className="h-6 px-2 text-xs text-blue-400 hover:text-blue-300 hover:bg-blue-400/10 gap-1.5"
											>
												<Volume2 className={cn("h-3.5 w-3.5", isTestingSound && "animate-pulse")} />
												<span className="sr-only">Test</span>
											</Button>
										</div>
									</div>
									<Select
										value={selectedAudioOutput}
										onValueChange={(value) => setSelectedAudioOutput(value)}
										disabled={!micPermissionGranted}
									>
										<SelectTrigger className="w-full bg-gray-800 border-gray-700 text-white">
											<SelectValue placeholder={micPermissionGranted === false ? "Grant microphone permission first" : "Select speaker"} />
										</SelectTrigger>
										<SelectContent className="bg-gray-800 border-gray-700">
											{audioOutputDevices.map((device) => (
												<SelectItem key={device.deviceId} value={device.deviceId} className="text-white">
													<span className="flex items-center gap-2 w-full">
														<Volume2 className="w-4 h-4 shrink-0" />
														<span className="truncate">{device.label}</span>
													</span>
												</SelectItem>
											))}
											{audioOutputDevices.length === 0 && (
												<div className="p-2 text-sm text-gray-400 text-center">
													No speakers found
												</div>
											)}
										</SelectContent>
									</Select>
								</div>
							</div>
						)}

						{/* Video Tab Content */}
						{activeTab === "video" && (
							<div className="space-y-6">
								{/* Quality Dropdown */}
								<div className="space-y-2">
									<label className="text-xs text-gray-400 font-medium uppercase tracking-wider">
										Quality
									</label>
									<Select
										value={videoQuality}
										onValueChange={(value) => setVideoQuality(value)}
									>
										<SelectTrigger className="w-full bg-gray-800 border-gray-700 text-white">
											<SelectValue placeholder="Select quality" />
										</SelectTrigger>
										<SelectContent className="bg-gray-800 border-gray-700">
											<SelectItem value="auto" className="text-white">
												<span className="flex items-center gap-2 w-full">
													<Sparkles className="w-4 h-4 shrink-0" />
													<span className="truncate">Auto (Recommended)</span>
												</span>
											</SelectItem>
											<SelectItem value="1080p" className="text-white">
												<span className="flex items-center gap-2 w-full">
													<Monitor className="w-4 h-4 shrink-0" />
													<span className="truncate">Full HD (1080p)</span>
												</span>
											</SelectItem>
											<SelectItem value="720p" className="text-white">
												<span className="flex items-center gap-2 w-full">
													<Monitor className="w-4 h-4 shrink-0" />
													<span className="truncate">High Definition (720p)</span>
												</span>
											</SelectItem>
											<SelectItem value="360p" className="text-white">
												<span className="flex items-center gap-2 w-full">
													<Signal className="w-4 h-4 shrink-0" />
													<span className="truncate">Standard Definition (360p)</span>
												</span>
											</SelectItem>
											<SelectItem value="240p" className="text-white">
												<span className="flex items-center gap-2 w-full">
													<Wifi className="w-4 h-4 shrink-0" />
													<span className="truncate">Low Bandwidth (240p)</span>
												</span>
											</SelectItem>
										</SelectContent>
									</Select>
								</div>

								{/* Camera Mode Dropdown - Only show on mobile devices */}
								{isMobile && (
									<div className="space-y-2">
										<label className="text-xs text-gray-400 font-medium uppercase tracking-wider">
											Camera Mode
										</label>
										<Select
											value={facingMode}
											onValueChange={(value: "user" | "environment") => setFacingMode(value)}
										>
											<SelectTrigger className="w-full bg-gray-800 border-gray-700 text-white">
												<SelectValue placeholder="Select mode" />
											</SelectTrigger>
											<SelectContent className="bg-gray-800 border-gray-700">
												<SelectItem value="user" className="text-white">
													<span className="flex items-center gap-2 w-full">
														<Users className="w-4 h-4 shrink-0" />
														<span className="truncate">Front Camera</span>
													</span>
												</SelectItem>
												<SelectItem value="environment" className="text-white">
													<span className="flex items-center gap-2 w-full">
														<Smartphone className="w-4 h-4 shrink-0" />
														<span className="truncate">Back Camera</span>
													</span>
												</SelectItem>
											</SelectContent>
										</Select>
									</div>
								)}


							</div>
						)}
					</div>

					{/* Footer - Join Button (Fixed at bottom) */}
					<div className="p-6 border-t border-gray-800">
						<Button
							onClick={handleJoinMeeting}
							disabled={isJoining || (!currentMediaStates.camera_enabled && !currentMediaStates.microphone_enabled)}
							className="w-full py-3 text-base font-semibold bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
						>
							{isJoining ? (
								<>
									<Loader2 className="mr-2 h-5 w-5 animate-spin" />
									Joining Meeting...
								</>
							) : (
								"Join Meeting"
							)}
						</Button>
					</div>
				</div>
			</div>
		</div>
	);
};

export default PublicMeetingOnboarding;